/**
 * Foco preso dentro de um diálogo — o que faltava para `aria-modal` ser verdade.
 *
 * O sistema tinha sete elementos declarando `role="dialog" aria-modal="true"` e
 * nenhum deles movia, prendia ou devolvia o foco. Esse atributo não é
 * decorativo: ele AFIRMA ao leitor de tela que o resto da página deixou de
 * existir. Sem o comportamento correspondente, abrir a gaveta de um cliente e
 * apertar Tab continuava passeando pela tabela atrás dela — anunciando linhas
 * que o olho não vê e o mouse não alcança.
 *
 * O navegador não ajuda aqui. Ele não tem noção de "modal" para um elemento
 * comum: para ele, a gaveta é só uma caixa que por acaso está por cima. O ciclo
 * do Tab precisa ser fechado à mão, e é o que este hook faz.
 *
 * Vive em `.ts` e não em `.tsx` porque um módulo que exporta componente E
 * não-componente quebra o fast refresh do Vite, e o eslint reprova
 * (`react-refresh/only-export-components`).
 */
import { useEffect, type RefObject } from 'react'

/**
 * O que conta como focável.
 *
 * `[tabindex="-1"]` fica de fora de propósito: é o que marca a camada de
 * clique-fora e o próprio painel, que recebem foco por código mas não devem
 * entrar no ciclo do Tab.
 */
const FOCAVEIS =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

/**
 * Prende o foco dentro de `painel` enquanto ele estiver montado.
 *
 * Faz as três partes que `aria-modal` promete:
 *
 * 1. **entra** — foca o primeiro elemento focável, ou o painel quando é só
 *    leitura (por isso o painel precisa de `tabIndex={-1}`);
 * 2. **fica** — Tab no último volta ao primeiro, Shift+Tab no primeiro vai ao
 *    último;
 * 3. **volta** — ao desmontar, devolve o foco a quem abriu. Sem isso o teclado
 *    reinicia no topo da página, e quem estava na linha 40 da tabela recomeça
 *    do começo.
 */
export function usarFocoPreso(painel: RefObject<HTMLElement | null>) {
  useEffect(() => {
    // Guardado na MONTAGEM: no fechamento o elemento pode já ter saído da
    // árvore — a linha da tabela que o filtro removeu, por exemplo — e aí
    // `document.activeElement` não serve mais de referência.
    const anterior = document.activeElement as HTMLElement | null

    const primeiro = painel.current?.querySelector<HTMLElement>(FOCAVEIS)
    ;(primeiro ?? painel.current)?.focus()

    const aoTeclar = (evento: KeyboardEvent) => {
      if (evento.key !== 'Tab' || !painel.current) return

      const focaveis = Array.from(painel.current.querySelectorAll<HTMLElement>(FOCAVEIS))
      if (focaveis.length === 0) return

      const inicio = focaveis[0]!
      const fim = focaveis[focaveis.length - 1]!
      const ativo = document.activeElement

      if (evento.shiftKey && (ativo === inicio || ativo === painel.current)) {
        evento.preventDefault()
        fim.focus()
      } else if (!evento.shiftKey && ativo === fim) {
        evento.preventDefault()
        inicio.focus()
      }
    }

    document.addEventListener('keydown', aoTeclar)
    return () => {
      document.removeEventListener('keydown', aoTeclar)
      // `isConnected`: devolver foco a um nó que saiu do documento não faz
      // nada, e o foco cairia no `<body>` — o mesmo que perdê-lo.
      if (anterior?.isConnected) anterior.focus()
    }
  }, [painel])
}

/**
 * Fecha no Esc.
 *
 * Separado do foco porque nem todo fechamento é imediato: a gaveta com
 * formulário precisa confirmar antes de descartar o rascunho, e passa uma
 * função que pergunta.
 */
export function usarEsc(aoFechar: () => void) {
  useEffect(() => {
    const aoTeclar = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') aoFechar()
    }
    document.addEventListener('keydown', aoTeclar)
    return () => document.removeEventListener('keydown', aoTeclar)
  }, [aoFechar])
}
